import { ArrowRight, ArrowUpRight, Clock, Briefcase, Palette, Mic2, Home as HomeIcon, Car } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AnimateIn from '../components/AnimateIn';

const Ecosystem = () => {
  const { t } = useLanguage();

  const livePlatforms = [
    {
      id: 'bitupros',
      title: t('bituPros'),
      desc: t('bituProsDesc'),
      button: t('enterPlatform'),
      link: 'https://bituprosolution.ca',
      icon: Briefcase,
      color: '#60A5FA',
      wallpaper: '/wallpapers/eco-bitupros.jpg',
    },
    {
      id: 'designermarket',
      title: t('designerMarket'),
      desc: t('designerMarketDesc'),
      button: t('explorePlatform'),
      link: 'https://designermarket.ca',
      icon: Palette,
      color: '#C084FC',
      wallpaper: '/wallpapers/eco-designermarket.jpg',
    },
    {
      id: 'streetz',
      title: t('streetzIndeed'),
      desc: t('streetzDesc'),
      button: t('explorePlatform'),
      link: 'https://streetzindeed.com',
      icon: Mic2,
      color: '#F87171',
      wallpaper: '/wallpapers/eco-streetz.jpg',
    },
    {
      id: 'altura',
      title: t('alturaLiving'),
      desc: t('alturaLivingDesc'),
      button: t('explorePlatform'),
      link: 'https://alturaliving.ca',
      icon: HomeIcon,
      color: '#FBBF24',
      wallpaper: '/wallpapers/eco-altura.jpg',
    },
    {
      id: 'ovigo',
      title: t('ovigo'),
      desc: t('ovigoDesc'),
      button: t('explorePlatform'),
      link: 'https://ovigo.ca',
      icon: Car,
      color: '#2DD4BF',
      wallpaper: '/wallpapers/eco-ovigo.jpg',
    },
  ];

  const comingSoon = [
    {
      id: 'mtl',
      title: t('mtlNetwork'),
      desc: t('mtlDesc'),
      color: '#A78BFA',
      wallpaper: '/wallpapers/eco-mtl.jpg',
    },
  ];

  return (
    <div data-testid="ecosystem-page">
      {/* Active Platforms */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 px-6 lg:px-8" data-testid="projects-section">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn className="text-center mb-16">
            <p className="accent-tag justify-center">{t('projectsTag')}</p>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white mb-4"
              style={{ fontFamily: "'Syne', sans-serif" }}
              data-testid="projects-title"
            >
              {t('projectsTitle')}
            </h2>
            <p className="text-base max-w-xl mx-auto text-gray-400">
              {t('projectsSubtitle')}
            </p>
          </AnimateIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {livePlatforms.map((project, i) => (
              <AnimateIn key={project.id} delay={i * 0.1} direction="up">
                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block h-[340px] rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1.5"
                  style={{ borderColor: `${project.color}30` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 20px 60px ${project.color}22`;
                    e.currentTarget.style.borderColor = `${project.color}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = `${project.color}30`;
                  }}
                  data-testid={`project-card-${project.id}`}
                >
                  <img
                    src={project.wallpaper}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.35) 0%, rgba(3,3,3,0.55) 40%, rgba(3,3,3,0.95) 100%)' }}
                  />
                  <div
                    className="absolute top-0 left-0 right-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity"
                    style={{ background: `linear-gradient(90deg, transparent, ${project.color}, transparent)` }}
                  />
                  <ArrowUpRight
                    size={20}
                    className="absolute top-5 right-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    style={{ color: project.color }}
                  />

                  <div className="absolute inset-x-0 bottom-0 p-7 md:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 backdrop-blur-sm"
                        style={{ background: `${project.color}1F`, border: `1px solid ${project.color}40` }}
                      >
                        <project.icon size={19} style={{ color: project.color }} />
                      </div>
                      <h3
                        className="text-xl font-bold"
                        style={{ fontFamily: "'Syne', sans-serif", color: project.color }}
                      >
                        {project.title}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-gray-300 mb-5">
                      {project.desc}
                    </p>
                    <div
                      className="flex items-center gap-2 text-sm font-semibold"
                      style={{ color: project.color }}
                    >
                      {project.button}
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </a>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-20 md:py-28 px-6 lg:px-8" style={{ background: '#0a0a0a' }} data-testid="coming-soon-section">
        <div className="max-w-[1200px] mx-auto">
          <AnimateIn className="text-center mb-16">
            <p className="accent-tag justify-center">{t('comingSoonTag')}</p>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white"
              style={{ fontFamily: "'Syne', sans-serif" }}
              data-testid="coming-soon-title"
            >
              {t('comingSoonTitle')}
            </h2>
          </AnimateIn>

          <div className="max-w-xl mx-auto">
            {comingSoon.map((item, i) => (
              <AnimateIn key={item.id} delay={i * 0.15}>
                <div
                  className="relative h-[260px] rounded-2xl overflow-hidden border"
                  style={{ borderColor: `${item.color}30` }}
                  data-testid={`coming-soon-card-${item.id}`}
                >
                  <img
                    src={item.wallpaper}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.4) 0%, rgba(3,3,3,0.95) 100%)' }}
                  />
                  <div
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ background: `linear-gradient(90deg, transparent, ${item.color}, transparent)` }}
                  />
                  <span
                    className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm"
                    style={{ background: `${item.color}22`, color: item.color, border: `1px solid ${item.color}40` }}
                  >
                    <Clock size={12} />
                    {t('comingSoonLabel')}
                  </span>
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <h3
                      className="text-xl font-bold mb-2"
                      style={{ fontFamily: "'Syne', sans-serif", color: item.color }}
                    >
                      {item.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-300">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ecosystem;
